import { environment } from '../../../../environments/environment';
import { BookedQuantity } from './booked-quantity';

export class BookingSlot {
  private _id: number;
  private _areaId: number;
  private _bookingDate: Date;
  private _fromTime: number;
  private _toTime: number;
  private _capacity: number;
  private _bookedQuantity: number;
  private _minPrice: number;
  private _nextSlotValid: boolean;
  areaItemsIds: number[];
  private _bookedDetailQuantity: BookedQuantity[];
  mergeSlots: BookingSlot[];

  constructor(
    id: number,
    areaId: number,
    bookingDate: Date,
    fromTime: number,
    toTime: number,
    capacity: number,
    bookedQuantity: number,
    minPrice?: number,
    nextSlotValid?: boolean,
    bookedDetailQuantity?: BookedQuantity[]
  ) {
    this._id = id;
    this._areaId = areaId;
    this._bookingDate = bookingDate;
    this._fromTime = fromTime;
    this._toTime = toTime;
    this._capacity = capacity;
    this._minPrice = minPrice;
    this._bookedQuantity = bookedQuantity;
    this._nextSlotValid = nextSlotValid;
    this._bookedDetailQuantity = bookedDetailQuantity;
  }

  static createFromAny(object: any): BookingSlot {
    if (object === null || object === undefined) {
      return null;
    }

    return new BookingSlot(
      object.id,
      object.areaId,
      new Date(object.bookingDate),
      object.fromTime,
      object.toTime,
      object.capacity,
      object.bookedQuantity,
      object.minPrice,
      object.nextSlotValid,
      object.bookedDetailQuantity ? object.bookedDetailQuantity.map(item => BookedQuantity.createFromAny(item)) : null,
    );
  }

  get id(): number {
    return this._id;
  }

  get areaId(): number {
    return this._areaId;
  }

  get bookingDate(): Date {
    return this._bookingDate;
  }

  get fromTime(): number {
    return this._fromTime;
  }

  get toTime(): number {
    return this._toTime;
  }

  get capacity(): number {
    return this._capacity;
  }

  get minPrice(): number {
    return this._minPrice;
  }

  set minPrice(value: number) {
    this._minPrice = value;
  }

  get bookedQuantity(): number {
    return this._bookedQuantity;
  }

  get freeQuantity(): number {
    return this._capacity - this._bookedQuantity;
  }

  get isCapacityAvailable(): boolean {
    if (this.freeQuantity != 0) {
      return true;
    }

    if (this.mergeSlots) {
      for (const slot of this.mergeSlots) {
        if (slot.freeQuantity != 0) {
          return true;
        }
      }
    }

    return false;
  }

  get slotDurationTime(): number {
    return this._toTime - this._fromTime;
  }

  get slotDurationTimeMinutes(): number {
    return (this._toTime - this._fromTime) * 60;
  }

  get minPriceStr(): string {
    return environment.globalSettings.toMoneyStr(this._minPrice);
  }

  get nextSlotValid(): boolean {
    if (this._nextSlotValid) {
      return true;
    }

    if (this.mergeSlots) {
      for (const slot of this.mergeSlots) {
        if (slot.nextSlotValid) {
          return true;
        }
      }
    }

    return false;
  }

  toString(): string {
    return environment.globalSettings.toTimeString(this._fromTime);
  }

  get bookedDetailQuantity(): BookedQuantity[] {
    return this._bookedDetailQuantity;
  }

  get availableAreaItemsIds(): number[] {
    if (!this.bookedDetailQuantity) {
      return [];
    }

    return this.bookedDetailQuantity.filter(item => this.areaItemsIds?.includes(item.areaItemId)).map(item => item.areaItemId);
  }

  getBookingSlot(areaId: number): BookingSlot {
    if (this._areaId === areaId) {
      return this;
    }

    if (this.mergeSlots) {
      for (const slot of this.mergeSlots) {
        if (slot.areaId === areaId) {
          return slot;
        }
      }
    }

    return null;
  }

  getBookingSlotId(areaId: number): number {
    const bookingSlot = this.getBookingSlot(areaId);
    return bookingSlot? bookingSlot.id : 0;
  }

  getDateTime(date: Date): Date {
    const selectedBookingDate = new Date(date);
    selectedBookingDate.setHours(0, environment.globalSettings.timeToMinutes(this.fromTime), 0, 0);
    return selectedBookingDate;
  }
}
